/**
 * Touch-sized pieces for the survey screens: bottom sheet, choice chips, steppers, fields that
 * commit when done (one undo step and one synced change per edit, not per keystroke), photos.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { getSymbol } from '@elec/symbols';
import { SymbolIcon } from './SymbolIcon.tsx';

/** Panel sliding up from the bottom on phones, centred on larger screens. */
export function Sheet(props: { title: string; onClose(): void; children: ReactNode; testId?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        props.onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
  return (
    <div className="sv-backdrop" onMouseDown={(e) => e.target === e.currentTarget && props.onClose()}>
      <div
        className="sv-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        tabIndex={-1}
        ref={ref}
        data-testid={props.testId}
      >
        <div className="sv-sheet-head">
          <h3>{props.title}</h3>
          <button className="sv-icon-btn" aria-label="×" onClick={props.onClose}>
            ✕
          </button>
        </div>
        <div className="sv-sheet-body">{props.children}</div>
      </div>
    </div>
  );
}

export function Chips<T extends string | number>(props: {
  value: T | undefined;
  options: Array<{ value: T; label: string }>;
  onChange(v: T | undefined): void;
  /** Tapping the selected chip clears it. */
  clearable?: boolean;
  testId?: string;
  label?: string;
}) {
  return (
    <div className="sv-chips" role="radiogroup" aria-label={props.label} data-testid={props.testId}>
      {props.options.map((o) => {
        const on = o.value === props.value;
        return (
          <button
            key={String(o.value)}
            role="radio"
            aria-checked={on}
            className={on ? 'on' : ''}
            onClick={() => props.onChange(on && props.clearable ? undefined : o.value)}
            data-value={o.value}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Stepper(props: {
  value: number;
  min?: number;
  onChange(v: number): void;
  label: string;
  testId?: string;
}) {
  const min = props.min ?? 1;
  return (
    <div className="sv-stepper" role="group" aria-label={props.label} data-testid={props.testId}>
      <button aria-label="−" disabled={props.value <= min} onClick={() => props.onChange(props.value - 1)}>
        −
      </button>
      <span aria-live="polite">{props.value}</span>
      <button aria-label="+" onClick={() => props.onChange(props.value + 1)}>
        +
      </button>
    </div>
  );
}

/** Text input that reports its value when editing ends (blur, Enter), if it changed. */
export function TextField(props: {
  label: string;
  value: string;
  onCommit(v: string): void;
  placeholder?: string;
  multiline?: boolean;
  list?: string;
  testId?: string;
  autoFocus?: boolean;
}) {
  const [v, setV] = useState(props.value);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setV(props.value);
  }, [props.value]);
  const commit = () => {
    focused.current = false;
    if (v !== props.value) props.onCommit(v);
  };
  const common = {
    value: v,
    placeholder: props.placeholder,
    onFocus: () => (focused.current = true),
    onBlur: commit,
    'data-testid': props.testId,
    'aria-label': props.label,
    autoFocus: props.autoFocus,
  };
  return (
    <label className="sv-field">
      <span>{props.label}</span>
      {props.multiline ? (
        <textarea rows={3} {...common} onChange={(e) => setV(e.target.value)} />
      ) : (
        <input
          {...common}
          list={props.list}
          enterKeyHint="done"
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
      )}
    </label>
  );
}

/** Number input (decimal keypad on phones); empty = undefined. */
export function NumberField(props: {
  label: string;
  value: number | undefined;
  onCommit(v: number | undefined): void;
  step?: number;
  testId?: string;
}) {
  const [v, setV] = useState(props.value === undefined ? '' : String(props.value));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setV(props.value === undefined ? '' : String(props.value));
  }, [props.value]);
  const commit = () => {
    focused.current = false;
    const n = v.trim() === '' ? undefined : Number(v.replace(',', '.'));
    if (n !== undefined && !Number.isFinite(n))
      return setV(props.value === undefined ? '' : String(props.value));
    if (n !== props.value) props.onCommit(n);
  };
  return (
    <label className="sv-field sv-num">
      <span>{props.label}</span>
      <input
        inputMode="decimal"
        value={v}
        aria-label={props.label}
        data-testid={props.testId}
        onFocus={() => (focused.current = true)}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        enterKeyHint="done"
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      />
    </label>
  );
}

export function PointIcon({ symbolId, size = 26 }: { symbolId: string; size?: number }) {
  const def = getSymbol(symbolId);
  return <span className="sv-point-icon">{def && <SymbolIcon def={def} size={size} />}</span>;
}

/** A stored photo shown from its bytes. */
export function PhotoThumb(props: {
  bytes: Uint8Array | undefined;
  alt: string;
  onClick?(): void;
  large?: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!props.bytes) return setUrl(null);
    const u = URL.createObjectURL(
      new Blob([props.bytes.slice().buffer as ArrayBuffer], { type: 'image/jpeg' }),
    );
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [props.bytes]);
  return (
    <button
      className={`sv-photo${props.large ? ' large' : ''}`}
      onClick={props.onClick}
      aria-label={props.alt}
    >
      {url ? <img src={url} alt={props.alt} /> : <span className="sv-photo-missing">…</span>}
    </button>
  );
}
